<template>
  <ion-page>
    <PageHeaderWithMenu title="Релизы" :scrolled="headerScrolled" />
    <ion-content :class="[isMobile ? 'main-background' : 'liga-web-background']" :fullscreen="true" @ionScroll="onContentScroll">
      
      <!-- Add New Program Button -->
      <ion-card class="admin-card">
        <ion-card-content>
          <ion-button expand="block" @click="openNewProgramModal()">
            <ion-icon slot="start" :icon="add"></ion-icon>
            Создать новую программу
          </ion-button>
          <ion-button
            v-if="showGoogleDriveSyncButton"
            expand="block"
            @click="syncWithGoogleDrive()"
            :disabled="syncing"
          >
            <ion-icon slot="start" :icon="cloudDownloadOutline"></ion-icon>
            {{ syncing ? 'Синхронизация...' : 'Синхронизировать с Google Drive' }}
          </ion-button>
        </ion-card-content>
      </ion-card>

      <!-- Programs list with releases -->
      <ion-card class="admin-card" v-for="program in programs" :key="program.id">
        <ion-card-header>
          <ion-card-title>{{ program.programName }}</ion-card-title>
          <p class="program-short-name">{{ program.shortName }}</p>
        </ion-card-header>
        <ion-card-content>
          <div class="program-logo-row">
            <div class="program-logo-wrapper">
              <img
                :src="programLogoUrl(program.shortName)"
                alt="Логотип программы"
                class="program-logo-img"
                @error="onLogoError(program.shortName)"
              />
            </div>
            <ion-button fill="clear" size="small" class="program-logo-edit-btn" @click="openLogoModal(program)">
              <ion-icon slot="start" :icon="imageOutline"></ion-icon>
              Изменить
            </ion-button>
          </div>
          <ion-button expand="block" @click="openNewReleaseModal(program)">
            <ion-icon slot="start" :icon="add"></ion-icon>
            Создать релиз
          </ion-button>
          
          <ion-list v-if="program.fullReleases && program.fullReleases.length">
            <ion-item
              v-for="release in program.fullReleases"
              :key="release.id"
              :detail="false"
              button
              @click="selectRelease(program, release)">
              <ion-label>
                <h2>{{ program.programName }} {{ release.releaseName }}</h2>
                <p>{{ release.visible ? 'Видимый' : 'Скрытый' }}</p>
              </ion-label>
              <ion-icon slot="end" :icon="chevronForward"></ion-icon>
            </ion-item>
          </ion-list>
          <p v-else class="empty-hint">Нет релизов для этой программы.</p>
        </ion-card-content>
      </ion-card>

      <!-- New Program Modal -->
      <ion-modal :is-open="showNewProgramModal" @didDismiss="closeNewProgramModal()" class="admin-releases-modal">
        <ion-header>
          <ion-toolbar>
            <ion-title>Новая программа</ion-title>
            <ion-buttons slot="end">
              <ion-button @click="closeNewProgramModal()">
                <ion-icon :icon="closeOutline"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content>
          <ion-card>
            <ion-card-header>
              <ion-card-title>Создать программу</ion-card-title>
            </ion-card-header>
            <ion-card-content>
              <ion-item>
                <ion-input label="Название программы" label-placement="stacked" v-model="newProgramName" 
                  placeholder="Например: HypeDance"></ion-input>
              </ion-item>
              <ion-item>
                <ion-input label="Короткое название (латиницей)" label-placement="stacked" v-model="newProgramShortName" 
                  placeholder="Например: HD"></ion-input>
              </ion-item>
              <ion-button expand="block" @click="createNewProgram()" :disabled="!newProgramName || !newProgramShortName || creatingProgram">
                {{ creatingProgram ? 'Создание...' : 'Создать программу' }}
              </ion-button>
            </ion-card-content>
          </ion-card>
        </ion-content>
      </ion-modal>

      <!-- New Release Modal -->
      <ion-modal :is-open="showNewReleaseModal" @didDismiss="closeNewReleaseModal()" class="admin-releases-modal">
        <ion-header>
          <ion-toolbar>
            <ion-title>Новый релиз</ion-title>
            <ion-buttons slot="end">
              <ion-button @click="closeNewReleaseModal()">
                <ion-icon :icon="closeOutline"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content>
          <ion-card v-if="selectedProgram">
            <ion-card-header>
              <ion-card-title>{{ selectedProgram.programName }}</ion-card-title>
            </ion-card-header>
            <ion-card-content>
              <ion-item>
                <ion-input label="Номер релиза" label-placement="stacked" v-model="newReleaseName" 
                  placeholder="Например: 1 или 10"></ion-input>
              </ion-item>
              <ion-item>
                <ion-input label="Описание (опционально)" label-placement="stacked" v-model="newReleaseDescription" 
                  placeholder="Описание релиза"></ion-input>
              </ion-item>
              <ion-item>
                <ion-toggle v-model="newReleaseVisible">Видимый для всех пользователей</ion-toggle>
              </ion-item>
              <ion-button expand="block" @click="createNewRelease()" :disabled="!newReleaseName || creating">
                {{ creating ? 'Создание...' : 'Создать' }}
              </ion-button>
            </ion-card-content>
          </ion-card>
        </ion-content>
      </ion-modal>

      <!-- Release Upload Modal -->
      <ion-modal :is-open="showUploadModal" @didDismiss="closeUploadModal()" class="admin-releases-modal">
        <ion-header>
          <ion-toolbar>
            <ion-title>Загрузка контента</ion-title>
            <ion-buttons slot="end">
              <ion-button @click="closeUploadModal()">
                <ion-icon :icon="closeOutline"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content v-if="selectedRelease && selectedProgram">
          <ion-card>
            <ion-card-header>
              <ion-card-title>{{ selectedProgram.programName }} {{ selectedRelease.releaseName }}</ion-card-title>
            </ion-card-header>
            <ion-card-content class="release-catalog-settings">
              <ion-item lines="full" class="transparent-item">
                <ion-toggle v-model="editReleaseVisible" :disabled="!selectedRelease">Видимый в каталоге</ion-toggle>
              </ion-item>
              <ion-item lines="full" class="transparent-item">
                <ion-input
                  v-model.number="editReleasePriceRub"
                  label="Цена, ₽ (0 — не продаётся отдельно; при тарифах ниже — запасной вариант)"
                  label-placement="floating"
                  type="number"
                  inputmode="decimal"
                  :disabled="!selectedRelease"
                />
              </ion-item>
              <div class="release-tiers-section">
                <h3>Тарифы покупки</h3>
                <p class="release-tiers-hint">Если заданы тарифы, используется минимальная цена из доступных пользователю. Роль ограничивает видимость тарифа.</p>
                <div v-for="(t, i) in editReleasePriceTiers" :key="i" class="tier-row">
                  <ion-input v-model="t.name" label="Название" label-placement="stacked" placeholder="Стандарт" />
                  <ion-input v-model.number="t.amountRub" label="₽" label-placement="stacked" type="number" min="0" />
                  <ion-select v-model="t.requiredAuthorityName" interface="popover" placeholder="Роль" cancel-text="Отмена">
                    <ion-select-option :value="null">—</ion-select-option>
                    <ion-select-option v-for="a in authorities" :key="a.name" :value="a.name">
                      {{ a.description || a.name }}
                    </ion-select-option>
                  </ion-select>
                  <ion-button fill="clear" size="small" @click="removeReleaseTier(i)">×</ion-button>
                </div>
                <ion-button fill="outline" size="small" @click="addReleaseTier">+ Тариф</ion-button>
              </div>
              <ion-button expand="block" @click="saveReleaseCatalogSettings" :disabled="!selectedRelease || savingReleaseCatalog">
                {{ savingReleaseCatalog ? 'Сохранение…' : 'Сохранить цену и видимость' }}
              </ion-button>
            </ion-card-content>
            <ion-card-content>
              <ion-card v-if="uploadQueue.length > 0" color="tertiary" class="upload-queue-card">
                <ion-card-header>
                  <ion-card-title>Имена на сервере</ion-card-title>
                  <p class="upload-queue-hint">
                    При необходимости измените имя каждого файла (включая ведущие нули), затем нажмите «Загрузить».
                    <template v-if="uploadQueueType === 'music' || uploadQueueType === 'video'">
                      Расширение совпадает с исходным файлом и не меняется.
                    </template>
                  </p>
                </ion-card-header>
                <ion-card-content>
                  <ion-list lines="full" class="transparent-list">
                    <ion-item v-for="(q, qIdx) in uploadQueue" :key="'q-' + qIdx">
                      <ion-label class="ion-text-wrap">
                        <p class="upload-queue-original">{{ q.file.name }}</p>
                        <ion-input
                          v-model="q.targetName"
                          label="Сохранить как"
                          label-placement="stacked"
                          :placeholder="q.file.name"
                          @ionInput="onReleaseUploadTargetNameInput(q, $event)"
                        ></ion-input>
                      </ion-label>
                    </ion-item>
                  </ion-list>
                  <ion-button expand="block" @click="flushUploadQueue()" :disabled="uploadQueueFlushing">
                    {{ uploadQueueFlushing ? 'Загрузка…' : 'Загрузить' }}
                  </ion-button>
                  <ion-button expand="block" fill="clear" color="medium" @click="clearUploadQueue()" :disabled="uploadQueueFlushing">
                    Отмена
                  </ion-button>
                </ion-card-content>
              </ion-card>

              <!-- Music -->
              <ion-item>
                <ion-label>
                  <h2>Музыка</h2>
                  <p>Аудио файлы (конвертируются в MP3)</p>
                </ion-label>
              </ion-item>
              <ion-button expand="block" @click="selectFiles('music')">
                <ion-icon slot="start" :icon="musicalNotesOutline"></ion-icon>
                Загрузить музыку
              </ion-button>
              <input
                type="file"
                ref="musicInput"
                multiple
                :accept="adminAudioFileAccept"
                style="display: none"
                @change="uploadFiles('music', $event)"
              />
              <div class="upload-modal-file-list">
                <ion-list v-if="releaseMediaFiles.music.length" class="transparent-list">
                  <ion-item v-for="f in releaseMediaFiles.music" :key="f.id" lines="full">
                    <ion-label>
                      <h3>{{ f.name }}</h3>
                      <p>{{ formatFileSize(f.weight) }}</p>
                    </ion-label>
                    <ion-button slot="end" fill="clear" @click="promptRenameFile('music', f)">
                      <ion-icon :icon="createOutline"></ion-icon>
                    </ion-button>
                    <ion-button slot="end" fill="clear" color="danger" @click="confirmDeleteFile('music', f)">
                      <ion-icon :icon="trashOutline"></ion-icon>
                    </ion-button>
                  </ion-item>
                </ion-list>
                <p v-else-if="!releaseMediaLoading" class="empty-hint">Нет загруженных файлов</p>
              </div>

              <!-- Video -->
              <ion-item>
                <ion-label>
                  <h2>Видео</h2>
                  <p>Видео файлы (конвертируются для стриминга)</p>
                </ion-label>
              </ion-item>
              <ion-button expand="block" @click="selectFiles('video')">
                <ion-icon slot="start" :icon="videocamOutline"></ion-icon>
                Загрузить видео
              </ion-button>
              <input type="file" ref="videoInput" multiple accept="video/*" style="display: none" @change="uploadFiles('video', $event)" />
              <div class="upload-modal-file-list">
                <ion-list v-if="releaseMediaFiles.video.length" class="transparent-list">
                  <ion-item v-for="f in releaseMediaFiles.video" :key="f.id" lines="full">
                    <ion-label>
                      <h3>{{ f.name }}</h3>
                      <p>{{ formatFileSize(f.weight) }}</p>
                    </ion-label>
                    <ion-button slot="end" fill="clear" @click="promptRenameFile('video', f)">
                      <ion-icon :icon="createOutline"></ion-icon>
                    </ion-button>
                    <ion-button slot="end" fill="clear" color="danger" @click="confirmDeleteFile('video', f)">
                      <ion-icon :icon="trashOutline"></ion-icon>
                    </ion-button>
                  </ion-item>
                </ion-list>
                <p v-else-if="!releaseMediaLoading" class="empty-hint">Нет загруженных файлов</p>
              </div>

              <!-- Guide (PDF) -->
              <ion-item>
                <ion-label>
                  <h2>Гайд (PDF)</h2>
                  <p>PDF файл (конвертируется в изображения)</p>
                </ion-label>
              </ion-item>
              <ion-button expand="block" @click="selectFiles('guide')">
                <ion-icon slot="start" :icon="documentTextOutline"></ion-icon>
                Загрузить гайд
              </ion-button>
              <input type="file" ref="guideInput" accept=".pdf,application/pdf" style="display: none" @change="uploadFiles('guide', $event)" />
              <div class="upload-modal-file-list">
                <ion-list v-if="releaseMediaFiles.guide.length" class="transparent-list">
                  <ion-item v-for="f in releaseMediaFiles.guide" :key="f.id" lines="full">
                    <ion-label>
                      <h3>{{ f.name }}</h3>
                      <p>{{ formatFileSize(f.weight) }}</p>
                    </ion-label>
                    <ion-button slot="end" fill="clear" @click="promptRenameFile('guide', f)">
                      <ion-icon :icon="createOutline"></ion-icon>
                    </ion-button>
                    <ion-button slot="end" fill="clear" color="danger" @click="confirmDeleteFile('guide', f)">
                      <ion-icon :icon="trashOutline"></ion-icon>
                    </ion-button>
                  </ion-item>
                </ion-list>
                <p v-else-if="!releaseMediaLoading" class="empty-hint">Нет загруженных PDF</p>
              </div>

              <!-- Cover -->
              <ion-item>
                <ion-label>
                  <h2>Обложка</h2>
                  <p>Изображение обложки релиза</p>
                </ion-label>
              </ion-item>
              <ion-button expand="block" @click="selectFiles('cover')">
                <ion-icon slot="start" :icon="imageOutline"></ion-icon>
                Загрузить обложку
              </ion-button>
              <input type="file" ref="coverInput" accept="image/*" style="display: none" @change="uploadFiles('cover', $event)" />
              <div class="upload-modal-file-list">
                <ion-list v-if="releaseMediaFiles.cover.length" class="transparent-list">
                  <ion-item v-for="f in releaseMediaFiles.cover" :key="f.id" lines="full">
                    <ion-label>
                      <h3>{{ f.name }}</h3>
                      <p>{{ formatFileSize(f.weight) }}</p>
                    </ion-label>
                    <ion-button slot="end" fill="clear" color="danger" @click="confirmDeleteFile('cover', f)">
                      <ion-icon :icon="trashOutline"></ion-icon>
                    </ion-button>
                  </ion-item>
                </ion-list>
                <p v-else-if="!releaseMediaLoading" class="empty-hint">Нет обложки</p>
              </div>

              <div v-if="uploadProgress.length > 0" class="upload-progress-list">
                <h3>Загрузка файлов</h3>
                <div v-for="(progress, idx) in uploadProgress" :key="'progress-' + idx" class="upload-progress-item">
                  <p>{{ progress.fileName }}</p>
                  <ion-progress-bar :value="progress.percent / 100"></ion-progress-bar>
                  <p class="progress-text">{{ progress.status }}</p>
                </div>
              </div>
              <ion-button expand="block" fill="outline" color="danger" class="ion-margin-top" @click="confirmDeleteRelease()">
                <ion-icon slot="start" :icon="trashOutline"></ion-icon>
                Удалить релиз
              </ion-button>
            </ion-card-content>
          </ion-card>
        </ion-content>
      </ion-modal>

      <!-- Program Logo Modal -->
      <ion-modal :is-open="showLogoModal" @didDismiss="closeLogoModal()" class="admin-releases-modal">
        <ion-header>
          <ion-toolbar>
            <ion-title>Логотип программы</ion-title>
            <ion-buttons slot="end">
              <ion-button @click="closeLogoModal()">
                <ion-icon :icon="closeOutline"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content v-if="logoModalProgram">
          <ion-card class="admin-card">
            <ion-card-header>
              <ion-card-title>{{ logoModalProgram.programName }}</ion-card-title>
              <p class="program-short-name">{{ logoModalProgram.shortName }}</p>
            </ion-card-header>
            <ion-card-content>
              <div class="program-logo-row" v-if="logoModalProgram.shortName">
                <div class="program-logo-wrapper program-logo-wrapper-preview">
                  <img
                    :src="programLogoUrl(logoModalProgram.shortName)"
                    alt="Логотип"
                    class="program-logo-img"
                    @error="onLogoError(logoModalProgram.shortName)"
                  />
                </div>
              </div>
              <input
                type="file"
                ref="logoFileInput"
                accept="image/png,image/jpeg,image/jpg"
                style="display: none"
                @change="onLogoFileSelected($event)"
              />
              <ion-button expand="block" @click="triggerLogoFileInput()" :disabled="logoUploading">
                <ion-icon slot="start" :icon="imageOutline"></ion-icon>
                {{ logoUploading ? 'Загрузка...' : 'Выбрать файл (PNG, JPG)' }}
              </ion-button>
              <ion-button expand="block" fill="outline" color="danger" class="ion-margin-top"
                @click="removeProgramLogo()" :disabled="logoDeleting || !logoHasFile(logoModalProgram.shortName)">
                <ion-icon slot="start" :icon="trashOutline"></ion-icon>
                {{ logoDeleting ? 'Удаление...' : 'Удалить логотип' }}
              </ion-button>
            </ion-card-content>
          </ion-card>
        </ion-content>
      </ion-modal>

    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { GlobalContext } from '@/model/globalcontext.model';
import { Program } from '@/model/program.model';
import { ProgramRelease } from '@/model/programrelease.model';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonProgressBar,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToggle,
  IonToolbar,
  alertController,
  toastController,
} from '@ionic/vue';
import { add, chevronForward, closeOutline, cloudDownloadOutline, createOutline, documentTextOutline, imageOutline, musicalNotesOutline, trashOutline, videocamOutline } from 'ionicons/icons';
import { defineComponent, inject } from 'vue';
import axios from 'axios';
import { ADMIN_AUDIO_FILE_ACCEPT } from '@/utils/admin-audio-upload-accept';
import { preserveFileExtension } from '@/utils/filename-extension';
import LogoService, { bundledProgramImageUrl } from '@/services/logo.service';
import AdminService from '@/services/admin.service';
import { Authority } from '@/model/authority.model';
import releasesService from '@/services/releases.service';

export default defineComponent({
  name: 'AdminReleases',
  components: {
    PageHeaderWithMenu,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonPage,
    IonProgressBar,
    IonSelect,
    IonSelectOption,
    IonTitle,
    IonToggle,
    IonToolbar,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    return {
      globalContext,
      isMobile: globalContext.isMobile,
      add,
      chevronForward,
      closeOutline,
      cloudDownloadOutline,
      documentTextOutline,
      imageOutline,
      musicalNotesOutline,
      trashOutline,
      videocamOutline,
      createOutline,
      adminAudioFileAccept: ADMIN_AUDIO_FILE_ACCEPT,
    };
  },
  data() {
    return {
      headerScrolled: false,
      programs: [] as Program[],
      showNewProgramModal: false,
      showNewReleaseModal: false,
      showUploadModal: false,
      showLogoModal: false,
      logoModalProgram: null as Program | null,
      logoUploading: false,
      logoDeleting: false,
      logoService: new LogoService(),
      programLogoSrc: {} as Record<string, string>,
      logoExistsForModal: false,
      selectedProgram: null as Program | null,
      selectedRelease: null as ProgramRelease | null,
      newProgramName: '',
      newProgramShortName: '',
      newReleaseName: '',
      newReleaseDescription: '',
      newReleaseVisible: false,
      creatingProgram: false,
      creating: false,
      showGoogleDriveSyncButton: false,
      syncing: false,
      uploadProgress: [] as Array<{fileName: string, percent: number, status: string}>,
      releaseMediaFiles: {
        music: [] as Array<{ id: string; name: string; weight: number }>,
        video: [] as Array<{ id: string; name: string; weight: number }>,
        guide: [] as Array<{ id: string; name: string; weight: number }>,
        cover: [] as Array<{ id: string; name: string; weight: number }>,
      },
      releaseMediaLoading: false,
      uploadQueue: [] as Array<{ file: File; targetName: string }>,
      uploadQueueType: null as 'music' | 'video' | 'guide' | 'cover' | null,
      uploadQueueFlushing: false,
      editReleaseVisible: false,
      editReleasePriceRub: 0 as number,
      editReleasePriceTiers: [] as { name: string; amountRub: number; requiredAuthorityName: string | null }[],
      authorities: [] as Authority[],
      adminService: new AdminService(),
      savingReleaseCatalog: false,
    };
  },
  mounted() {
    this.loadPrograms();
    this.loadAuthorities();
  },
  methods: {
    async loadAuthorities() {
      try {
        this.authorities = await this.adminService.getAuthoritiesFull();
      } catch {
        this.authorities = [];
      }
    },
    onContentScroll(ev: any) {
      this.headerScrolled = ev.detail.scrollTop > 0;
    },
    
    async loadPrograms() {
      try {
        const response = await axios.get('/api/programs-with-releases');
        this.programs = response.data;
        await this.loadProgramLogos();
      } catch (error) {
        console.error('Failed to load programs:', error);
        const toast = await toastController.create({
          message: 'Не удалось загрузить список программ',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      }
    },

    async loadProgramLogos() {
      const list = this.programs || [];
      await Promise.allSettled(
        list.map(async (p) => {
          const sn = p.shortName;
          if (!sn) return;
          try {
            const url = await this.logoService.getProgramLogo(sn);
            this.programLogoSrc[sn] = url;
          } catch {
            this.programLogoSrc[sn] = bundledProgramImageUrl(sn);
          }
        })
      );
    },

    programLogoUrl(shortName: string | undefined): string {
      const name = shortName ?? '';
      if (!name) return bundledProgramImageUrl(undefined);
      return this.programLogoSrc[name] ?? bundledProgramImageUrl(name);
    },

    openNewProgramModal() {
      this.newProgramName = '';
      this.newProgramShortName = '';
      this.showNewProgramModal = true;
    },

    closeNewProgramModal() {
      this.showNewProgramModal = false;
      this.newProgramName = '';
      this.newProgramShortName = '';
    },

    async createNewProgram() {
      if (!this.newProgramName || !this.newProgramShortName) return;
      
      this.creatingProgram = true;
      try {
        await axios.post('/api/programs', {
          programName: this.newProgramName,
          shortName: this.newProgramShortName,
        });
        
        const toast = await toastController.create({
          message: 'Программа создана',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
        
        this.closeNewProgramModal();
        this.loadPrograms();
      } catch (error) {
        console.error('Failed to create program:', error);
        const toast = await toastController.create({
          message: 'Ошибка создания программы',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.creatingProgram = false;
      }
    },

    async syncWithGoogleDrive() {
      this.syncing = true;
      try {
        const response = await axios.post('/api/admin/releases/sync-from-google');
        
        const toast = await toastController.create({
          message: `Синхронизация запущена. ${response.data.message || ''}`,
          duration: 5000,
          color: 'success',
        });
        await toast.present();
        
        // Reload programs after sync
        setTimeout(() => {
          this.loadPrograms();
        }, 2000);
      } catch (error) {
        console.error('Failed to sync with Google Drive:', error);
        const toast = await toastController.create({
          message: 'Ошибка синхронизации с Google Drive',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.syncing = false;
      }
    },

    openNewReleaseModal(program: Program) {
      this.selectedProgram = program;
      this.newReleaseName = '';
      this.newReleaseDescription = '';
      this.newReleaseVisible = false;
      this.showNewReleaseModal = true;
    },

    closeNewReleaseModal() {
      this.showNewReleaseModal = false;
      this.selectedProgram = null;
    },

    async createNewRelease() {
      if (!this.selectedProgram || !this.newReleaseName) return;
      
      this.creating = true;
      try {
        await axios.post('/api/releases', {
          releaseName: this.newReleaseName,
          releaseDescription: this.newReleaseDescription || null,
          visible: this.newReleaseVisible,
          programId: this.selectedProgram.id,
        });
        
        const toast = await toastController.create({
          message: 'Релиз создан',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
        
        this.closeNewReleaseModal();
        this.loadPrograms();
      } catch (error) {
        console.error('Failed to create release:', error);
        const toast = await toastController.create({
          message: 'Ошибка создания релиза',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.creating = false;
      }
    },

    selectRelease(program: Program, release: ProgramRelease) {
      this.selectedProgram = program;
      this.selectedRelease = release;
      this.editReleaseVisible = !!release.visible;
      const pk = release.priceKopecks;
      this.editReleasePriceRub =
        pk != null && pk > 0 ? Math.round(pk) / 100 : 0;
      this.showUploadModal = true;
      this.uploadProgress = [];
      this.loadReleasePriceTiers(release.id);
      this.loadReleaseMediaLists();
    },

    async loadReleasePriceTiers(releaseId?: number) {
      if (!releaseId) {
        this.editReleasePriceTiers = [];
        return;
      }
      try {
        const tiers = await releasesService.getPriceTiers(releaseId);
        this.editReleasePriceTiers = tiers.map((t) => ({
          name: t.name,
          amountRub: t.amountKopecks > 0 ? t.amountKopecks / 100 : 0,
          requiredAuthorityName: t.requiredAuthorityName ?? null,
        }));
      } catch {
        this.editReleasePriceTiers = [];
      }
    },

    addReleaseTier() {
      this.editReleasePriceTiers.push({ name: '', amountRub: 0, requiredAuthorityName: null });
    },

    removeReleaseTier(index: number) {
      this.editReleasePriceTiers.splice(index, 1);
    },

    async saveReleaseCatalogSettings() {
      const rel = this.selectedRelease;
      if (!rel?.id) return;
      this.savingReleaseCatalog = true;
      try {
        const rub = Number(this.editReleasePriceRub);
        const priceKopecks = !Number.isFinite(rub) || rub <= 0 ? 0 : Math.round(rub * 100);
        await axios.patch(
          `/api/releases/${rel.id}`,
          { id: rel.id, visible: this.editReleaseVisible, priceKopecks },
          { headers: { 'Content-Type': 'application/merge-patch+json' } }
        );
        const tiersPayload = this.editReleasePriceTiers
          .filter((t) => t.name.trim())
          .map((t) => ({
            name: t.name.trim(),
            amountKopecks: Math.max(0, Math.round(Number(t.amountRub) * 100)),
            requiredAuthorityName: t.requiredAuthorityName || null,
          }));
        await releasesService.setPriceTiers(rel.id, tiersPayload);
        rel.visible = this.editReleaseVisible;
        rel.priceKopecks = priceKopecks;
        const toast = await toastController.create({
          message: 'Сохранено',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error(e);
        const toast = await toastController.create({
          message: 'Не удалось сохранить',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.savingReleaseCatalog = false;
      }
    },

    onLogoError(shortName: string | undefined) {
      if (shortName == null || shortName === '') return;
      this.logoService.forgetProgramLogo(shortName);
      this.programLogoSrc[shortName] = bundledProgramImageUrl(shortName);
    },

    async openLogoModal(program: Program) {
      const shortName = program.shortName ?? '';
      if (!shortName) return;
      this.logoModalProgram = program;
      this.showLogoModal = true;
      this.logoExistsForModal = false;
      try {
        const res = await axios.get(`/api/admin/program-logos/${encodeURIComponent(shortName)}/exists`);
        this.logoExistsForModal = res.data === true;
      } catch {
        this.logoExistsForModal = false;
      }
    },

    closeLogoModal() {
      this.showLogoModal = false;
      this.logoModalProgram = null;
      this.logoUploading = false;
      this.logoDeleting = false;
    },

    logoHasFile(shortName: string | undefined): boolean {
      if (shortName == null) return false;
      return this.logoModalProgram?.shortName === shortName && this.logoExistsForModal;
    },

    triggerLogoFileInput() {
      const input = this.$refs.logoFileInput as HTMLInputElement | undefined;
      input?.click();
    },

    onLogoFileSelected(ev: Event) {
      const input = ev.target as HTMLInputElement;
      const file = input?.files?.[0];
      if (!file || !this.logoModalProgram) return;
      this.uploadLogo(file);
      input.value = '';
    },

    async uploadLogo(file: File) {
      const program = this.logoModalProgram;
      const shortName = program?.shortName ?? '';
      if (!program || !shortName) return;
      this.logoUploading = true;
      try {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`/api/admin/program-logos/${encodeURIComponent(shortName)}`, formData);
        this.logoExistsForModal = true;
        this.logoService.forgetProgramLogo(shortName);
        this.programLogoSrc[shortName] = await this.logoService.getProgramLogo(shortName, true);
        const toast = await toastController.create({
          message: 'Логотип загружен',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
      } catch (error) {
        console.error('Failed to upload logo', error);
        const toast = await toastController.create({
          message: 'Ошибка загрузки логотипа',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.logoUploading = false;
      }
    },

    async removeProgramLogo() {
      const program = this.logoModalProgram;
      const shortName = program?.shortName ?? '';
      if (!program || !shortName) return;
      this.logoDeleting = true;
      try {
        await axios.delete(`/api/admin/program-logos/${encodeURIComponent(shortName)}`);
        this.logoExistsForModal = false;
        this.logoService.forgetProgramLogo(shortName);
        this.programLogoSrc[shortName] = bundledProgramImageUrl(shortName);
        const toast = await toastController.create({
          message: 'Логотип удалён',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
      } catch (error) {
        console.error('Failed to delete logo', error);
        const toast = await toastController.create({
          message: 'Ошибка удаления логотипа',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.logoDeleting = false;
      }
    },

    closeUploadModal() {
      this.showUploadModal = false;
      this.selectedRelease = null;
      this.selectedProgram = null;
      this.uploadProgress = [];
      this.clearUploadQueue();
    },

    clearUploadQueue() {
      this.uploadQueue = [];
      this.uploadQueueType = null;
      this.uploadQueueFlushing = false;
    },

    onReleaseUploadTargetNameInput(q: { file: File; targetName: string }, ev: CustomEvent) {
      const raw = (ev.detail as { value?: string }).value ?? '';
      const fixed = preserveFileExtension(q.file.name, raw);
      if (fixed !== q.targetName) {
        q.targetName = fixed;
      }
    },

    async flushUploadQueue() {
      if (!this.uploadQueueType || !this.selectedRelease || this.uploadQueue.length === 0) return;
      this.uploadQueueFlushing = true;
      const type = this.uploadQueueType;
      const items = [...this.uploadQueue];
      this.uploadQueue = [];
      this.uploadQueueType = null;
      try {
        for (const item of items) {
          const raw = item.targetName?.trim() ? item.targetName.trim() : item.file.name;
          const name =
            type === 'music' || type === 'video' ? preserveFileExtension(item.file.name, raw) : raw;
          await this.uploadSingleFile(type, item.file, name);
        }
      } finally {
        this.uploadQueueFlushing = false;
      }
    },

    async loadReleaseMediaLists() {
      if (!this.selectedRelease) return;
      this.releaseMediaLoading = true;
      const id = this.selectedRelease.id;
      try {
        const [music, video, guide, cover] = await Promise.all([
          axios.get(`/api/releases/${id}/media/music`).then(r => r.data),
          axios.get(`/api/releases/${id}/media/video`).then(r => r.data),
          axios.get(`/api/releases/${id}/media/guide`).then(r => r.data),
          axios.get(`/api/releases/${id}/media/cover`).then(r => r.data),
        ]);
        this.releaseMediaFiles = { music, video, guide, cover };
      } catch (error) {
        console.error('Failed to load release media lists', error);
        const toast = await toastController.create({
          message: 'Не удалось загрузить списки файлов',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.releaseMediaLoading = false;
      }
    },

    formatFileSize(bytes: number): string {
      if (bytes < 1024) return `${bytes} Б`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
    },

    async confirmDeleteFile(subdir: 'music' | 'video' | 'guide' | 'cover', f: { id: string; name: string }) {
      const typeLabel = subdir === 'music' ? 'музыку' : subdir === 'video' ? 'видео' : subdir === 'guide' ? 'PDF' : 'обложку';
      const alert = await alertController.create({
        header: 'Удалить файл?',
        message: `Удалить ${typeLabel}: ${f.name}?${subdir === 'guide' ? ' Будут также удалены все сконвертированные страницы (изображения).' : ''}`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          { text: 'Удалить', role: 'destructive', handler: () => this.deleteFile(subdir, f) },
        ],
      });
      await alert.present();
    },

    async promptRenameFile(subdir: 'music' | 'video' | 'guide' | 'cover', f: { id: string; name: string }) {
      const selected = this.selectedRelease;
      if (!selected) return;
      const releaseId = selected.id;
      const lockExt = subdir === 'music' || subdir === 'video';
      const alert = await alertController.create({
        header: 'Переименовать файл',
        message: lockExt
          ? `Текущее имя: ${f.name}. Расширение файла остаётся прежним.`
          : `Текущее имя: ${f.name}`,
        inputs: [
          {
            name: 'newName',
            type: 'text',
            value: f.name,
            placeholder: lockExt ? 'Новое имя без смены расширения' : 'Новое имя с расширением',
          },
        ],
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Сохранить',
            handler: async (data) => {
              const payload = data as { newName?: string };
              const raw = payload?.newName?.trim();
              if (!raw) return true;
              const to = lockExt ? preserveFileExtension(f.id, raw) : raw;
              if (to === f.id) return true;
              try {
                await axios.put(`/api/admin/releases/${releaseId}/file/rename`, null, {
                  params: { subdir, from: f.id, to },
                });
                const toast = await toastController.create({
                  message: `Файл переименован в «${to}»`,
                  duration: 2000,
                  color: 'success',
                });
                await toast.present();
                await this.loadReleaseMediaLists();
              } catch (error) {
                console.error('Rename failed', error);
                const toast = await toastController.create({
                  message: 'Не удалось переименовать',
                  duration: 3000,
                  color: 'danger',
                });
                await toast.present();
              }
              return true;
            },
          },
        ],
      });
      await alert.present();
    },

    async deleteFile(subdir: 'music' | 'video' | 'guide' | 'cover', f: { id: string; name: string }) {
      if (!this.selectedRelease) return;
      try {
        await axios.delete(`/api/admin/releases/${this.selectedRelease.id}/file`, {
          params: { subdir, filename: f.id },
        });
        const toast = await toastController.create({
          message: `${f.name} удалён`,
          duration: 2000,
          color: 'success',
        });
        await toast.present();
        await this.loadReleaseMediaLists();
      } catch (error) {
        console.error('Delete file failed', error);
        const toast = await toastController.create({
          message: 'Ошибка удаления файла',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      }
    },

    async confirmDeleteRelease() {
      if (!this.selectedRelease || !this.selectedProgram) return;
      const alert = await alertController.create({
        header: 'Удалить релиз?',
        message: `Релиз «${this.selectedProgram.programName} ${this.selectedRelease.releaseName}» будет удалён. Это действие нельзя отменить.`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          { text: 'Удалить', role: 'destructive', handler: () => this.deleteRelease() },
        ],
      });
      await alert.present();
    },

    async deleteRelease() {
      if (!this.selectedRelease?.id) return;
      try {
        await axios.delete(`/api/releases/${this.selectedRelease.id}`);
        const toast = await toastController.create({
          message: 'Релиз удалён',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
        this.closeUploadModal();
        this.loadPrograms();
      } catch (error) {
        console.error('Failed to delete release:', error);
        const toast = await toastController.create({
          message: 'Ошибка удаления релиза',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      }
    },

    selectFiles(type: 'music' | 'video' | 'guide' | 'cover') {
      const refName = (
        {
          music: 'musicInput',
          video: 'videoInput',
          guide: 'guideInput',
          cover: 'coverInput',
        } as const
      )[type];
      const r = this.$refs[refName] as HTMLInputElement | HTMLInputElement[] | undefined;
      const input = Array.isArray(r) ? r[0] : r;
      input?.click();
    },

    async uploadFiles(type: 'music' | 'video' | 'guide' | 'cover', event: Event) {
      if (!this.selectedRelease) return;

      const input = event.target as HTMLInputElement;
      const files = input.files;
      if (!files || files.length === 0) return;

      this.uploadQueueType = type;
      this.uploadQueue = Array.from(files).map((file) => ({
        file,
        targetName: file.name,
      }));
      input.value = '';
    },

    async uploadSingleFile(type: string, file: File, serverFileName?: string) {
      if (!this.selectedRelease) return;

      const displayName = serverFileName && serverFileName.trim() ? serverFileName.trim() : file.name;

      this.uploadProgress.push({
        fileName: displayName,
        percent: 0,
        status: 'Загрузка...',
      });
      const progressIndex = this.uploadProgress.length - 1;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', displayName);

      const url = `/api/admin/releases/${this.selectedRelease.id}/upload/${type}`;
      const token =
        localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');

      try {
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', url);
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.upload.onprogress = (ev: ProgressEvent) => {
            if (ev.lengthComputable && ev.total > 0) {
              const percent = Math.round((100 * ev.loaded) / ev.total);
              const item = this.uploadProgress[progressIndex];
              if (item) {
                item.percent = percent;
                item.status = percent >= 100 ? 'Загружено' : 'Загрузка...';
              }
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else {
              const e = new Error(`Upload failed: ${xhr.status}`) as Error & { status?: number };
              e.status = xhr.status;
              reject(e);
            }
          };
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send(formData);
        });

        const item = this.uploadProgress[progressIndex];
        if (item) {
          item.percent = 100;
          item.status = 'Загружено';
        }
        await this.loadReleaseMediaLists();
        const toast = await toastController.create({
          message: `${displayName} загружен`,
          duration: 2000,
          color: 'success',
        });
        await toast.present();
      } catch (error) {
        console.error('Upload failed:', error);
        const item = this.uploadProgress[progressIndex];
        if (item) item.status = 'Ошибка';
        const err = error as Error & { status?: number; response?: { status?: number } };
        const status = err.status ?? err.response?.status;
        const toastMessage =
          status === 413
            ? `Файл слишком большой (413). Увеличьте client_max_body_size в nginx и лимиты multipart/undertow в Spring (сейчас до 5 ГБ на сервере).`
            : `Ошибка загрузки ${displayName}`;
        const toast = await toastController.create({
          message: toastMessage,
          duration: status === 413 ? 5000 : 3000,
          color: 'danger',
        });
        await toast.present();
      }
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  background: var(--liga-surface, rgba(255, 255, 255, 0.05));
  backdrop-filter: blur(10px);
  --color: var(--liga-chrome-text);
}

.admin-card ion-card-title {
  color: var(--liga-chrome-text);
  font-weight: 600;
}

.program-short-name {
  font-size: 0.85rem;
  color: var(--liga-chrome-text-muted);
  margin-top: 4px;
}

.empty-hint {
  color: var(--liga-chrome-text-muted);
  font-style: italic;
  padding: 8px 0;
}

.program-logo-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

/* Логотипы обычно 3000×406 (широкий прямоугольник) */
.program-logo-wrapper {
  aspect-ratio: 3000 / 406;
  height: 40px;
  width: auto;
  max-width: 200px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--liga-surface, rgba(255, 255, 255, 0.08));
  flex-shrink: 0;
}

.program-logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.program-logo-wrapper-preview {
  aspect-ratio: 3000 / 406;
  height: 80px;
  width: auto;
  max-width: 100%;
  min-width: 120px;
}

/* Убрать окантовку у кнопки «Изменить» */
.program-logo-edit-btn {
  --border-width: 0;
  --box-shadow: none;
}
.program-logo-edit-btn.ion-focused,
.program-logo-edit-btn:hover,
.program-logo-edit-btn.ion-activated {
  --border-width: 0;
  --box-shadow: none;
}

/* Прозрачные списки как в тренингах */
.admin-card ion-list {
  background: transparent;
}

.admin-card ion-item {
  --background: transparent;
  --background-activated: transparent;
  --background-hover: transparent;
  --background-focused: transparent;
  --color: var(--liga-chrome-text);
  --border-color: var(--liga-surface-border);
}

.admin-card ion-item::part(native) {
  background: transparent;
}

.admin-card ion-item ion-label h2 {
  color: var(--liga-chrome-text);
  font-weight: 500;
}

.admin-card ion-item ion-label p {
  color: var(--liga-chrome-text-muted);
  font-size: 0.9em;
}

.admin-card ion-item ion-icon {
  color: var(--ion-color-orange);
}

/* Кнопки в стиле приложения: оранжевые */
.admin-card ion-button:not([fill="clear"]) {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --color: var(--liga-chrome-text);
  --color-activated: white;
  font-weight: bold;
  --border-radius: 14px;
  margin-top: 8px;
}

.admin-card ion-button[fill="clear"] {
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  font-weight: 600;
}

/* Прогресс загрузки */
.upload-progress-list {
  margin-top: 20px;
}

.upload-progress-item {
  margin-bottom: 12px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}

.upload-progress-item p {
  margin: 4px 0;
  font-size: 0.9rem;
  color: var(--liga-chrome-text);
}

.progress-text {
  color: var(--liga-chrome-text-muted);
  font-size: 0.8rem;
}

.release-tiers-section {
  margin: 12px 0 16px;
}
.release-tiers-section h3 {
  margin: 0 0 6px;
  font-size: 16px;
  color: var(--liga-chrome-text);
}
.release-tiers-hint {
  margin: 0 0 10px;
  font-size: 13px;
  color: var(--liga-chrome-text-muted);
}
.tier-row {
  display: grid;
  grid-template-columns: 1fr 90px 1fr auto;
  gap: 8px;
  align-items: end;
  margin-bottom: 8px;
}
@media (max-width: 720px) {
  .tier-row {
    grid-template-columns: 1fr;
  }
}
.upload-progress-item ion-progress-bar {
  --progress-background: var(--ion-color-orange);
  --background: rgba(255, 255, 255, 0.1);
}
</style>
